<script setup>
import "daterangepicker/daterangepicker.css";
import "daterangepicker/daterangepicker.js";
import { ref, onMounted, computed, nextTick } from "vue";
import moment from "moment";
import DateRangePicker from "daterangepicker";

import { useExpenseStore } from "@/stores/expenseStore";
import Modal from "bootstrap/js/dist/modal";
import ExpensesModal from "@/components/modal/expenses-modal.vue";

const title = "Expenses";
const text = "Finance & Accounts";
const text1 = "Expenses";

const expenseStore = useExpenseStore();

const dateRangeInput = ref(null);
const editItem = ref(null);

const columns = [
  { sorter: false },
  { title: "Expense Name", key: "Name", dataIndex: "name" },
  { title: "Date", key: "Date", dataIndex: "expense_date" },
  { title: "Category", key: "Category", dataIndex: "category" },
  { title: "Payment Method", key: "PaymentMethod", dataIndex: "payment_method" },
  { title: "Amount", key: "Amount", dataIndex: "amount" },
  { title: "Status", key: "Status", dataIndex: "status" },
  { title: "", key: "action", sorter: false },
];

const tableData = computed(() => expenseStore.items || []);
const stats = computed(() => expenseStore.stats);

const amountRanges = [
  { label: "All", min: null, max: null },
  { label: "0 - 1,000", min: 0, max: 1000 },
  { label: "1,000 - 5,000", min: 1000, max: 5000 },
  { label: "5,000 - 10,000", min: 5000, max: 10000 },
  { label: "10,000+", min: 10000, max: null },
];

const amountLabel = computed(() => {
  const { min_amount, max_amount } = expenseStore.filters;
  const found = amountRanges.find(
    (r) =>
      (r.min ?? null) === (min_amount ?? null) &&
      (r.max ?? null) === (max_amount ?? null)
  );
  return found ? found.label : "Custom";
});

const statusLabel = computed(() => expenseStore.filters.status || "All");

const sortMode = ref("recent");
const sortLabel = computed(() => {
  switch (sortMode.value) {
    case "amount_desc":
      return "Amount (High → Low)";
    case "amount_asc":
      return "Amount (Low → High)";
    default:
      return "Last 7 Days";
  }
});

function fmtAmount(v) {
  return Number(v || 0).toLocaleString();
}

function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

function setAmountRange(range) {
  expenseStore.filters.min_amount = range.min;
  expenseStore.filters.max_amount = range.max;
  expenseStore.filters.page = 1;
  reload();
}

function setStatus(status) {
  expenseStore.filters.status = status || "";
  expenseStore.filters.page = 1;
  reload();
}

function setSort(mode) {
  sortMode.value = mode;
  // Currently backend sorts by recent; extend if you add sort params.
  reload();
}

function applyDateRange() {
  const val = dateRangeInput.value?.value || "";
  const parts = val.split("-").map((s) => s.trim());
  if (parts.length === 2) {
    const from = moment(parts[0], ["DD/MM/YYYY", "M/D/YYYY"]).format(
      "YYYY-MM-DD"
    );
    const to = moment(parts[1], ["DD/MM/YYYY", "M/D/YYYY"]).format(
      "YYYY-MM-DD"
    );
    if (from !== "Invalid date") expenseStore.filters.date_from = from;
    if (to !== "Invalid date") expenseStore.filters.date_to = to;
    expenseStore.filters.page = 1;
    reload();
  }
}

async function reload(extra = {}) {
  if (!extra.per_page && expenseStore.filters.per_page > 200) {
    expenseStore.filters.per_page = 200;
  }
  // keep list + stats in sync with same filters
  await Promise.all([
    expenseStore.fetchAll(extra),
    expenseStore.fetchStats(extra),
  ]);
}

function reloadSafePerPage() {
  if (expenseStore.filters.per_page > 200)
    expenseStore.filters.per_page = 200;
  expenseStore.filters.page = 1;
  reload();
}

function openAdd() {
  editItem.value = null;
  nextTick(() => {
    const el = document.getElementById("add_expenses");
    Modal.getOrCreateInstance(el)?.show();
  });
}

function openEdit(row) {
  editItem.value = row;
  nextTick(() => {
    const el = document.getElementById("add_expenses");
    Modal.getOrCreateInstance(el)?.show();
  });
}

function openDelete(row) {
  editItem.value = row;
  nextTick(() => {
    const el = document.getElementById("delete_expenses");
    Modal.getOrCreateInstance(el)?.show();
  });
}

function exportExcel() {
  const rows = sortedData.value;
  if (!rows.length) return;

  const header = [
    "Expense Name",
    "Date",
    "Category",
    "Payment Method",
    "Amount",
    "Currency",
    "Status",
  ];

  const lines = [header.map(toCsvValue).join(",")];

  rows.forEach((r) => {
    const row = [
      r.name,
      r.expense_date || "",
      r.category || "",
      r.payment_method || "",
      r.amount,
      r.currency || "",
      r.status || "",
    ];
    lines.push(row.map(toCsvValue).join(","));
  });

  const blob = new Blob([lines.join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute(
    "download",
    `expenses-report-${new Date().toISOString().slice(0, 10)}.csv`
  );
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

async function exportPdf() {
  try {
    const blob = await expenseStore.exportPdf(); // uses current filters

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `expenses-report-${new Date()
      .toISOString()
      .slice(0, 10)}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (e) {
    // error already toasted by store
    console.error("Export PDF failed", e);
  }
}

onMounted(async () => {
  // initial load
  await reload();

  if (dateRangeInput.value) {
    const start = moment().subtract(6, "days");
    const end = moment();
    dateRangeInput.value.value = `${start.format("DD/MM/YYYY")} - ${end.format(
      "DD/MM/YYYY"
    )}`;

    new DateRangePicker(
      dateRangeInput.value,
      {
        startDate: start,
        endDate: end,
        ranges: {
          Today: [moment(), moment()],
          Yesterday: [
            moment().subtract(1, "days"),
            moment().subtract(1, "days"),
          ],
          "Last 7 Days": [moment().subtract(6, "days"), moment()],
          "Last 30 Days": [moment().subtract(29, "days"), moment()],
          "This Month": [moment().startOf("month"), moment().endOf("month")],
          "Last Month": [
            moment().subtract(1, "month").startOf("month"),
            moment().subtract(1, "month").endOf("month"),
          ],
        },
      },
      () => applyDateRange()
    );
  }
});
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">
      <!-- Breadcrumb -->
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div
          class="d-flex my-xl-auto right-content align-items-center flex-wrap"
        >
          <!-- Export -->
          <div class="me-2 mb-2">
            <div class="dropdown">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                <i class="ti ti-file-export me-1"></i>Export
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="exportPdf"
                    v-can="'Expense.Export'"
                    ><i class="ti ti-file-type-pdf me-1"></i>Export as PDF</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="exportExcel"
                    v-can="'Expense.Export'"
                    ><i class="ti ti-file-type-xls me-1"></i>Export as Excel
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <!-- Add Expense -->
          <div class="mb-2">
            <a
              href="javascript:void(0);"
              class="btn btn-primary d-flex align-items-center"
              @click="openAdd"
              v-can="'Expense.Create'"
            >
              <i class="ti ti-circle-plus me-2"></i>Add Expense
            </a>
          </div>

          <!-- Collapse header -->
          <div class="head-icons ms-2">
            <a
              href="javascript:void(0);"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              data-bs-original-title="Collapse"
              id="collapse-header"
              @click="toggleHeader"
            >
              <i class="ti ti-chevrons-up"></i>
            </a>
          </div>
        </div>
      </div>
      <!-- /Breadcrumb -->

      <!-- Summary cards -->
      <div class="row mb-4">
        <div class="col-xl-3 col-md-6 mb-3">
          <div class="card h-100">
            <div class="card-body">
              <p class="text-muted mb-1">Total Expenses</p>
              <h4 class="mb-0">
                KES {{ fmtAmount(stats.total) }}
              </h4>
              <small class="text-muted">{{ stats.count }} records</small>
            </div>
          </div>
        </div>
        <div class="col-xl-3 col-md-6 mb-3">
          <div class="card h-100">
            <div class="card-body">
              <p class="text-muted mb-1">Pending</p>
              <h4 class="mb-0">
                KES {{ fmtAmount(stats.pending) }}
              </h4>
            </div>
          </div>
        </div>
        <div class="col-xl-3 col-md-6 mb-3">
          <div class="card h-100">
            <div class="card-body">
              <p class="text-muted mb-1">Approved</p>
              <h4 class="mb-0">
                KES {{ fmtAmount(stats.approved) }}
              </h4>
            </div>
          </div>
        </div>
        <div class="col-xl-3 col-md-6 mb-3">
          <div class="card h-100">
            <div class="card-body">
              <p class="text-muted mb-1">Paid</p>
              <h4 class="mb-0">
                KES {{ fmtAmount(stats.paid) }}
              </h4>
            </div>
          </div>
        </div>
      </div>

      <!-- List card -->
      <div class="card">
        <div
          class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3"
        >
          <h5>Expenses List</h5>
          <div
            class="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3"
          >
            <!-- Date range -->
            <div class="me-3">
              <div class="input-icon-end position-relative">
                <input
                  type="text"
                  class="form-control date-range bookingrange"
                  ref="dateRangeInput"
                  placeholder="dd/mm/yyyy - dd/mm/yyyy"
                />
                <span class="input-icon-addon">
                  <i class="ti ti-chevron-down"></i>
                </span>
              </div>
            </div>

            <!-- Amount filter -->
            <div class="dropdown me-3">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                {{ amountLabel }}
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li
                  v-for="r in amountRanges"
                  :key="r.label"
                  @click="setAmountRange(r)"
                >
                  <a href="javascript:void(0);" class="dropdown-item rounded-1">
                    {{ r.label }}
                  </a>
                </li>
              </ul>
            </div>

            <!-- Status filter -->
            <div class="dropdown me-3">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                Status: {{ statusLabel }}
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="setStatus('')"
                    >All</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="setStatus('Pending')"
                    >Pending</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="setStatus('Approved')"
                    >Approved</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="setStatus('Paid')"
                    >Paid</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="setStatus('Cancelled')"
                    >Cancelled</a
                  >
                </li>
              </ul>
            </div>

            <!-- Sort -->
            <div class="dropdown">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                Sort By : {{ sortLabel }}
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="setSort('recent')"
                    >Last 7 Days</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="setSort('amount_desc')"
                    >Amount (High → Low)</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="setSort('amount_asc')"
                    >Amount (Low → High)</a
                  >
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Table -->
        <div class="card-body p-0">
          <div class="custom-datatable-filter table-responsive">
            <a-table
              class="table datatable thead-light"
              :columns="columns"
              :data-source="tableData"
              :loading="expenseStore.loading"
              :pagination="false"
              row-key="id"
            >
              <template #bodyCell="{ column, record }">
                <!-- Name -->
                <template v-if="column.key === 'Name'">
                  <h6 class="fw-medium fs-14">
                    {{ record.name }}
                  </h6>
                </template>

                <!-- Date -->
                <template v-else-if="column.key === 'Date'">
                  <span>{{ record.expense_date || "-" }}</span>
                </template>

                <!-- Category -->
                <template v-else-if="column.key === 'Category'">
                  <span>{{ record.category || "-" }}</span>
                </template>

                <!-- Payment Method -->
                <template v-else-if="column.key === 'PaymentMethod'">
                  <span>{{ record.payment_method || "-" }}</span>
                </template>

                <!-- Amount -->
                <template v-else-if="column.key === 'Amount'">
                  <span>
                    {{ record.currency || "KES" }}
                    {{ Number(record.amount || 0).toLocaleString() }}
                  </span>
                </template>

                <!-- Status -->
                <template v-else-if="column.key === 'Status'">
                  <span
                    :class="[
                      'badge',
                      record.status === 'Pending'
                        ? 'badge-warning'
                        : record.status === 'Approved'
                        ? 'badge-info'
                        : record.status === 'Paid'
                        ? 'badge-success'
                        : 'badge-danger',
                      'badge-xs',
                      'align-items-center',
                      'd-inline-flex',
                    ]"
                  >
                    <i class="ti ti-point-filled me-1"></i>{{ record.status }}
                  </span>
                </template>

                <!-- Actions -->
                <template v-else-if="column.key === 'action'">
                  <div class="action-icon d-inline-flex">
                    <a
                      href="javascript:void(0);"
                      class="me-2"
                      @click="openEdit(record)"
                      v-can="'Expense.Edit'"
                    >
                      <i class="ti ti-edit"></i>
                    </a>
                    <a
                      href="javascript:void(0);"
                      @click="openDelete(record)"
                      v-can="'Expense.Delete'"
                    >
                      <i class="ti ti-trash"></i>
                    </a>
                  </div>
                </template>

                <!-- Fallback -->
                <template v-else>
                  <span>{{ record[column.dataIndex] }}</span>
                </template>
              </template>
            </a-table>
          </div>

          <!-- Pagination -->
          <div
            class="p-3 d-flex justify-content-between align-items-center"
          >
            <div class="d-flex align-items-center gap-3">
              <small>Total: {{ expenseStore.pagination.total }}</small>
              <div class="d-flex align-items-center">
                <label class="me-2">Per page</label>
                <select
                  class="form-select form-select-sm"
                  style="width: auto"
                  v-model.number="expenseStore.filters.per_page"
                  @change="reloadSafePerPage"
                >
                  <option :value="10">10</option>
                  <option :value="25">25</option>
                  <option :value="50">50</option>
                  <option :value="100">100</option>
                  <option :value="200">200</option>
                </select>
              </div>
            </div>
            <div class="d-flex align-items-center">
              <button
                class="btn btn-sm btn-light me-2"
                :disabled="expenseStore.pagination.current_page <= 1"
                @click="
                  reload({
                    page: expenseStore.pagination.current_page - 1,
                  })
                "
              >
                Prev
              </button>
              <button
                class="btn btn-sm btn-light"
                :disabled="
                  expenseStore.pagination.current_page *
                    expenseStore.pagination.per_page >=
                  expenseStore.pagination.total
                "
                @click="
                  reload({
                    page: expenseStore.pagination.current_page + 1,
                  })
                "
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3"
    >
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by
        <a href="javascript:void(0);" class="text-primary"
          >DoneQuick Technological World</a
        >
      </p>
    </div>
  </div>

  <!-- Shared Add/Edit/Delete Modal -->
  <expenses-modal :editItem="editItem" @saved="reload()" />
</template>

<style scoped>

</style>


